import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const MODULE_META = {
  content:   { label: 'Content Briefs', icon: '📝', color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe', route: '/content' },
  creatives: { label: 'Creatives',        icon: '🎨', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a', route: '/creatives' },
  postcopy:  { label: 'Post Copy',        icon: '✍️', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0', route: '/postcopy' },
  videoworks:{ label: 'Video Works',      icon: '📹', color: '#ef4444', bg: '#fef2f2', border: '#fecaca', route: '/videoworks' },
  printmedia:{ label: 'Print Media',       icon: '🖨️', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', route: '/printmedia' },
  specialdays:{ label: 'Special Days',     icon: '🎉', color: '#ec4899', bg: '#fdf2f8', border: '#fbcfe8', route: '/special-days' },
};

const STAGE_META = {
  'Pending Internal': { key: 'internal', tab: 'internal', color: '#e87830', bg: '#fff7ed', border: '#fed7aa', label: 'Internal Review', icon: '⏳' },
  'Pending Client':   { key: 'client',   tab: 'client',   color: '#6c58f0', bg: '#f5f3ff', border: '#ddd6fe', label: 'Client Review',   icon: '👔' },
};

export default function Approvals() {
  const navigate = useNavigate();
  const [items, setItems]     = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending_internal: 0, pending_client: 0, by_module: {} });
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState({});
  const [sending, setSending] = useState({});
  const [tokens, setTokens] = useState({}); // { "moduleKey:itemId": token }

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/approvals');
        setItems(r.data.data || []);
        setSummary(r.data.summary || {});
        try {
          const rem = await api.get('/approvals/last-reminders');
          setReminders(rem.data.data || {});
        } catch (_) {}
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const stages = ['Pending Internal', 'Pending Client'];
  const moduleOrder = ['content', 'creatives', 'postcopy', 'videoworks', 'printmedia', 'specialdays'];

  const goToItem = (item) => {
    const mm = MODULE_META[item.module_key];
    const sm = STAGE_META[item.stage];
    if (!mm || !sm) return;
    if (item.module_key === 'specialdays') {
      navigate(`${mm.route}?month=${item.detail?.month}`);
      return;
    }
    navigate(`${mm.route}?tab=${sm.tab}&wo=${item.wo_number}`);
  };

  const buildReminderMessage = (item) => {
    const mm = MODULE_META[item.module_key];
    const greeting = item.stage === 'Pending Internal' ? 'Approver' : (item.client || 'Client');
    if (item.module_key === 'specialdays') {
      const token = item.detail?.token;
      const link = token ? `${window.location.origin}/select/${token}` : '';
      const linkLine = link ? `\n\nYou can make your selections using this link:\n${link}` : '\n\nPlease log in to your Qlicxo WorkOps portal to submit your selections.';
      return `Hi ${greeting},\n\nThis is a gentle reminder to complete your Special Days selection for ${item.title}.${linkLine}\n\nPlease submit at your earliest convenience.\n\nThank you!`;
    }
    const woNum = item.wo_number ? ` (${item.wo_number})` : '';
    const stage = item.stage === 'Pending Client' ? 'client review' : 'internal review';
    return `Hi ${greeting},\n\nThis is a gentle reminder regarding ${mm?.label || item.module}${woNum} — "${item.title}".\n\nThis item is pending your ${stage}. Please log in to your Qlicxo WorkOps portal to review and approve.\n\nThank you!`;
  };

  const generateTokenForItem = async (item) => {
    if (item.module_key === 'specialdays') return null;
    const tokenKey = `${item.module_key}:${item.item_id}`;
    if (tokens[tokenKey]) return tokens[tokenKey];
    try {
      const MODEL_MAP = { postcopy: 'App\\Models\\PostCopy', creatives: 'App\\Models\\Creative', content: 'App\\Models\\ContentCalendarScript', videoworks: 'App\\Models\\VideoWork' };
      const res = await api.post('/approval-tokens/generate', {
        item_id: item.item_id,
        item_type: MODEL_MAP[item.module_key],
        module_key: item.module_key,
        work_order_id: item.work_order_id,
        stage: item.stage,
      });
      const tok = res.data?.data?.token;
      if (tok) {
        setTokens(prev => ({ ...prev, [tokenKey]: tok }));
        return tok;
      }
    } catch (e) { console.error('Token generation failed', e); }
    return null;
  };

  const buildMessageWithToken = (item, reviewToken) => {
    const mm = MODULE_META[item.module_key];
    const greeting = item.stage === 'Pending Internal' ? 'Approver' : (item.client || 'Client');
    if (item.module_key === 'specialdays') return buildReminderMessage(item);
    const woNum = item.wo_number ? ` (${item.wo_number})` : '';
    const stage = item.stage === 'Pending Client' ? 'client review' : 'internal review';
    const reviewLink = reviewToken ? `${window.location.origin}/review/${reviewToken}` : '';
    const reviewLine = reviewLink
      ? `\n\nYou can review and approve directly using this link:\n${reviewLink}`
      : '\n\nPlease log in to your Qlicxo WorkOps portal to review and approve.';
    return `Hi ${greeting},\n\nThis is a gentle reminder regarding ${mm?.label || item.module}${woNum} — "${item.title}".\n\nThis item is pending your ${stage}.${reviewLine}\n\nThank you!`;
  };

  const handleCopyReminder = async (item) => {
    let msg;
    if (item.module_key !== 'specialdays') {
      const tok = await generateTokenForItem(item);
      msg = buildMessageWithToken(item, tok);
    } else {
      msg = buildReminderMessage(item);
    }
    try {
      await navigator.clipboard.writeText(msg);
      setSending(prev => ({ ...prev, [item.id]: 'copied' }));
      setTimeout(() => setSending(prev => ({ ...prev, [item.id]: null })), 2000);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleSendReminder = async (item) => {
    if (!item.client_email) {
      alert('No email address found for this client.');
      return;
    }
    setSending(prev => ({ ...prev, [item.id]: 'sending' }));
    let msg;
    if (item.module_key !== 'specialdays') {
      const tok = await generateTokenForItem(item);
      msg = buildMessageWithToken(item, tok);
    } else {
      msg = buildReminderMessage(item);
    }
    const mm = MODULE_META[item.module_key];
    const subject = `Reminder: ${mm?.label || item.module} — ${item.title} awaiting review`;
    try {
      await api.post('/touchpoint-messages/send-email', {
        to_email: item.client_email,
        to_name: item.client || null,
        subject,
        body: msg,
        client_id: item.client_id || null,
        touchpoint_type: `approval_reminder:${item.id}`,
      });
      setSending(prev => ({ ...prev, [item.id]: 'sent' }));
      setReminders(prev => ({ ...prev, [item.id]: new Date().toISOString() }));
      setTimeout(() => setSending(prev => ({ ...prev, [item.id]: null })), 2500);
    } catch (e) {
      console.error('Send failed', e);
      const errMsg = e?.response?.data?.message || 'Failed to send reminder.';
      alert(errMsg);
      setSending(prev => ({ ...prev, [item.id]: null }));
    }
  };

  if (loading) return (
    <div className="page-wrap" style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
      Loading approvals...
    </div>
  );

  return (
    <div className="page-wrap">
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <SummaryCard label="Total Pending" value={summary.total} color="#6366f1" />
        <SummaryCard label="Internal Review" value={summary.pending_internal} color="#e87830" />
        <SummaryCard label="Client Review" value={summary.pending_client} color="#6c58f0" />
        {moduleOrder.map(mod => (
          <SummaryCard key={mod} label={MODULE_META[mod].label} value={summary.by_module?.[mod] || 0} color={MODULE_META[mod].color} />
        ))}
      </div>

      {/* Grouped sections */}
      {stages.map(stage => {
        const sm = STAGE_META[stage];
        const stageItems = items.filter(i => i.stage === stage);
        if (stageItems.length === 0) return null;

        return (
          <div key={stage} style={{ marginBottom: 28 }}>
            {/* Stage header */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
              paddingBottom: 8, borderBottom: `2px solid ${sm.color}`,
            }}>
              <span style={{ fontSize: '1.1rem' }}>{sm.icon}</span>
              <span style={{ fontSize: '1rem', fontWeight: 700, color: sm.color }}>{sm.label}</span>
              <span style={{
                fontSize: '0.72rem', fontWeight: 700, padding: '2px 10px', borderRadius: 10,
                background: sm.bg, color: sm.color, border: `1px solid ${sm.border}`,
              }}>{stageItems.length} pending</span>
            </div>

            {/* Module sub-sections */}
            {moduleOrder.map(mod => {
              const mm = MODULE_META[mod];
              const modItems = stageItems.filter(i => i.module_key === mod);
              if (modItems.length === 0) return null;

              return (
                <div key={mod} style={{ marginBottom: 16, marginLeft: 4 }}>
                  {/* Module header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ width: 4, height: 16, borderRadius: 2, background: mm.color, display: 'inline-block' }} />
                    <span style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text)' }}>{mm.icon} {mm.label}</span>
                    <span style={{
                      fontSize: '0.68rem', fontWeight: 600, padding: '1px 8px', borderRadius: 8,
                      background: mm.bg, color: mm.color, border: `1px solid ${mm.border}`,
                    }}>{modItems.length}</span>
                  </div>

                  {/* Item rows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {modItems.map(item => (
                      <ItemRow key={item.id} item={item} sm={sm} onNavigate={() => goToItem(item)}
                        lastReminded={reminders[item.id]} sendState={sending[item.id]}
                        onCopy={() => handleCopyReminder(item)} onSend={() => handleSendReminder(item)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Empty state */}
      {items.length === 0 && (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '2rem', marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>All caught up!</div>
          <div style={{ fontSize: '0.8rem', marginTop: 4 }}>No pending approvals right now.</div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="card" style={{ padding: '14px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function ItemRow({ item, sm, onNavigate, lastReminded, sendState, onCopy, onSend }) {
  const preview = (() => {
    const d = item.detail || {};
    if (item.module_key === 'content') return d.hook || d.caption || d.script || '';
    if (item.module_key === 'creatives') return d.notes || d.image_name || '';
    if (item.module_key === 'specialdays') return d.client_note || `${d.selected}/${d.allowance} days selected`;
    return d.caption || '';
  })();

  return (
    <div className="card" onClick={onNavigate} style={{
      padding: '12px 16px', cursor: 'pointer', display: 'flex',
      alignItems: 'center', gap: 12, transition: 'box-shadow 0.15s, transform 0.1s',
    }}
      onMouseOver={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseOut={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
    >
      {/* Left: WO info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', fontWeight: 700, color: 'var(--accent)' }}>
            {item.wo_number}
          </span>
          <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text)' }}>{item.title}</span>
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Client: {item.client || '—'}</span>
          {item.event_date && <span>· Event: {new Date(item.event_date + 'T12:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>}
          {preview && <span style={{ color: 'var(--text-muted)', opacity: 0.7 }}>· {preview.substring(0, 60)}{preview.length > 60 ? '...' : ''}</span>}
        </div>
      </div>

      {/* Right: reminder buttons + platforms + arrow */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {/* Reminder indicator & buttons */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          {lastReminded && (
            <span style={{
              fontSize: '0.7rem', color: '#059669', whiteSpace: 'nowrap', marginRight: 2,
              background: '#ecfdf5', padding: '3px 8px', borderRadius: 6, fontWeight: 600,
              border: '1px solid #a7f3d0',
            }}>
              Reminded {new Date(lastReminded).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </span>
          )}
          <button
            onClick={onCopy}
            title="Copy reminder message"
            style={{
              border: '1px solid #d1d5db', background: sendState === 'copied' ? '#d1fae5' : '#fff', cursor: 'pointer',
              borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700,
              color: sendState === 'copied' ? '#059669' : '#374151', transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
            }}
          >
            {sendState === 'copied' ? '✓ Copied' : '📋 Copy'}
          </button>
          <button
            onClick={onSend}
            disabled={sendState === 'sending' || sendState === 'sent'}
            title={item.client_email ? `Send reminder to ${item.client_email}` : 'No client email'}
            style={{
              border: '1px solid #c7d2fe', cursor: sendState === 'sending' ? 'wait' : 'pointer',
              background: sendState === 'sent' ? '#d1fae5' : sendState === 'sending' ? '#dbeafe' : '#eef2ff',
              borderRadius: 8, padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700,
              color: sendState === 'sent' ? '#059669' : sendState === 'sending' ? '#3b82f6' : '#4f46e5',
              transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
              opacity: item.client_email ? 1 : 0.4,
            }}
          >
            {sendState === 'sent' ? '✓ Sent' : sendState === 'sending' ? '⏳ Sending...' : '📧 Remind'}
          </button>
        </div>

        {item.platforms && item.platforms.length > 0 && (
          <div style={{ display: 'flex', gap: 3 }}>
            {item.platforms.slice(0, 3).map((p, i) => (
              <span key={i} style={{
                fontSize: '0.68rem', fontWeight: 600, padding: '2px 6px', borderRadius: 5,
                background: '#dbeafe', color: '#1e40af',
              }}>{p}</span>
            ))}
            {item.platforms.length > 3 && (
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>+{item.platforms.length - 3}</span>
            )}
          </div>
        )}
        {item.submitted_at && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {new Date(item.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
          </span>
        )}
        <span style={{ fontSize: '1rem', color: sm.color, fontWeight: 700 }}>→</span>
      </div>
    </div>
  );
}
